import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRefreshModeFromQuery, type RefreshMode } from "../../hooks/useRouteParams";

export interface RefreshControlsProps {
  setRefreshToken: React.Dispatch<React.SetStateAction<number>>;
  isFetching?: boolean;
}

function modeToMs(mode: RefreshMode): number | null {
  switch (mode) {
    case "2s":
      return 2_000;
    case "30s":
      return 30_000;
    case "1m":
      return 60_000;
    case "5m":
      return 5 * 60_000;
    case "off":
    default:
      return null;
  }
}

const OPTIONS: { id: RefreshMode; label: string }[] = [
  { id: "off", label: "Off" },
  { id: "2s", label: "2s" },
  { id: "30s", label: "30s" },
  { id: "1m", label: "1m" },
  { id: "5m", label: "5m" },
];

export function RefreshControls({ setRefreshToken, isFetching = false }: RefreshControlsProps) {
  const { refreshMode, setRefreshMode } = useRefreshModeFromQuery();
  const refreshIntervalMs = useMemo(() => modeToMs(refreshMode), [refreshMode]);

  const [progressToNextRefresh, setProgressToNextRefresh] = useState(0);
  // const [waitingForData, setWaitingForData] = useState(false);
  const lastRefreshAtRef = useRef(Date.now());

  const canManualRefresh = refreshMode === "off";

  useEffect(() => {
    if (isFetching) {
      setProgressToNextRefresh(1);
      return;
    }

    if (!refreshIntervalMs) {
      return;
    }

    lastRefreshAtRef.current = Date.now();

    setProgressToNextRefresh(0);

    const intervalMs = 50;
    const intervalId = window.setInterval(() => {
      const now = Date.now();
      const elapsed = now - lastRefreshAtRef.current;
      const progress = Math.min(1, elapsed / refreshIntervalMs);

      if (progress >= 1) {
        setRefreshToken((t) => t + 1);
        
        clearInterval(intervalId);
      } else {
        setProgressToNextRefresh(progress);
      }
    }, intervalMs);

    return () => window.clearInterval(intervalId);
  }, [refreshIntervalMs, isFetching]);

  const manualRefresh = () => {
    if (!canManualRefresh) return;

    setRefreshToken((t) => t + 1);
  };

  const showProgressRing = refreshMode !== "off" && refreshIntervalMs != null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "nowrap" }}>
      <Tooltip title="Auto refresh interval" placement="top">
        <ToggleButtonGroup
          size="small"
          color="primary"
          exclusive
          value={refreshMode}
          onChange={(_e, next: RefreshMode | null) => {
            if (!next) return;
            setRefreshMode(next);
          }}
          sx={{ height: 40 }}
        >
          {OPTIONS.map((opt) => (
            <ToggleButton key={opt.id} value={opt.id} sx={{ height: 40, px: 1.25, minWidth: 48 }}>
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Tooltip>

      {showProgressRing ? (
        <Tooltip
          title={
            isFetching && progressToNextRefresh >= 1
              ? "Refreshing…"
              : `Next refresh in ${Math.ceil((1 - progressToNextRefresh) * (refreshIntervalMs! / 1000))}s`
          }
          placement="top"
        >
          <IconButton
            size="small"
            onClick={manualRefresh}
            disabled={!canManualRefresh}
            sx={{
              height: 40,
              width: 40,
              borderRadius: 1,
              border: "1px solid rgba(148,163,184,0.35)",
            }}
          >
            <CircularProgress
              variant="determinate"
              value={(1 - progressToNextRefresh) * 100}
              size={24}
              thickness={4}
              sx={{
                height: 40,
                width: 40,
                borderRadius: 1,
                border: "1px solid rgba(148,163,184,0.35)",
                color: "primary.main"
              }}
            />
          </IconButton>
        </Tooltip>
      ) : (
        <IconButton
          size="small"
          onClick={manualRefresh}
          disabled={!canManualRefresh}
          sx={{
            height: 40,
            width: 40,
            borderRadius: 1,
            border: "1px solid rgba(148,163,184,0.35)",
          }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      )}

    </Box>
  );
}
